// ---------------------------------------------------------------------------
// Dashboard chart primitives (CLE-257).
//
// Lightweight, dependency-light visual building blocks used across the command
// center: count-up KPI numbers, inline SVG sparklines, SVG progress rings, and
// recharts-backed donut + grouped-bar charts. All styled for the Firmio dark
// theme with the brand palette (blue / purple / green / red).
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_TOOLTIP_ITEM_STYLE, CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// Firmio brand palette (matches index.css --chart-* tokens).
export const FIRMIO_COLORS = {
  blue: '#3B82F6',
  purple: '#8B5CF6',
  green: '#10B981',
  red: '#F87171',
  lightBlue: '#60A5FA',
  lightPurple: '#A78BFA',
} as const;

// Rotating palette for categorical charts (donut slices, team avatars, …).
export const CHART_PALETTE = [
  '#3B82F6',
  '#8B5CF6',
  '#10B981',
  '#F59E0B',
  '#60A5FA',
  '#A78BFA',
  '#34D399',
  '#F87171',
  '#22D3EE',
  '#FB923C',
];

/** Deterministic color for a string key (person/project), stable across renders. */
export function colorForKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return CHART_PALETTE[hash % CHART_PALETTE.length];
}

// --- Count-up number --------------------------------------------------------

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Animates a number from 0 up to `value` once on mount. Honors the user's
 * reduced-motion preference by skipping the animation.
 */
export function CountUp({
  value,
  format,
  durationMs = 900,
  className,
}: {
  value: number;
  format: (n: number) => string;
  durationMs?: number;
  className?: string;
}) {
  const [display, setDisplay] = useState(() => (prefersReducedMotion() ? value : 0));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(value);
      return;
    }
    const start = performance.now();
    const from = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (value - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [value, durationMs]);

  return <span className={className}>{format(display)}</span>;
}

// --- Sparkline (inline SVG) -------------------------------------------------

/**
 * Tiny inline trend line. Renders a smooth-ish polyline plus an optional area
 * fill. Designed to sit inside a KPI card.
 */
export function Sparkline({
  data,
  color = FIRMIO_COLORS.blue,
  width = 96,
  height = 28,
  className,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  className?: string;
}) {
  if (!data || data.length < 2) {
    return <svg width={width} height={height} className={className} aria-hidden />;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / span) * (height - 4) - 2;
    return [x, y] as const;
  });
  const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${points[0][0]},${height} ${line} ${points[points.length - 1][0]},${height}`;
  const gradId = `spark-${color.replace('#', '')}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradId})`} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- Progress ring (inline SVG) ---------------------------------------------

/**
 * Circular progress indicator. `value` is 0-100. Color is driven by `health`
 * (green / amber / red) or an explicit `color`.
 */
export function ProgressRing({
  value,
  size = 56,
  stroke = 6,
  color,
  label,
  className,
}: {
  value: number;
  size?: number;
  stroke?: number;
  color?: string;
  label?: string;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const ringColor =
    color ?? (clamped >= 75 ? FIRMIO_COLORS.green : clamped >= 40 ? '#F59E0B' : FIRMIO_COLORS.red);

  return (
    <div className={`relative inline-flex items-center justify-center ${className ?? ''}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(148,163,184,0.18)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
        />
      </svg>
      <span className="absolute text-xs font-semibold text-foreground">
        {label ?? `${Math.round(clamped)}%`}
      </span>
    </div>
  );
}

// --- Donut chart (recharts) -------------------------------------------------

interface DonutDatum {
  name: string;
  value: number;
}

export function DonutChart({
  data,
  height = 240,
  formatValue,
}: {
  data: DonutDatum[];
  height?: number;
  formatValue?: (n: number) => string;
}) {
  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground/70">Nu există date.</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          stroke="none"
        >
          {data.map((entry, i) => (
            <Cell key={entry.name} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={CHART_TOOLTIP_STYLE}
          itemStyle={CHART_TOOLTIP_ITEM_STYLE}
          formatter={(v: number) => (formatValue ? formatValue(v) : v)}
        />
        <Legend
          verticalAlign="bottom"
          iconType="circle"
          wrapperStyle={{ fontSize: 11, color: '#94A3B8' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

// --- Grouped bar chart (recharts) -------------------------------------------

interface BarDatum {
  label: string;
  income: number;
  expenses: number;
}

export function IncomeExpenseBarChart({
  data,
  height = 260,
  formatValue,
}: {
  data: BarDatum[];
  height?: number;
  formatValue?: (n: number) => string;
}) {
  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground/70">Nu există date.</p>;
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} barGap={4} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
        <XAxis
          dataKey="label"
          tick={{ fill: '#94A3B8', fontSize: 11 }}
          axisLine={{ stroke: 'rgba(148,163,184,0.18)' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fill: '#94A3B8', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={48}
          tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
        />
        <Tooltip
          cursor={{ fill: 'rgba(148,163,184,0.08)' }}
          contentStyle={CHART_TOOLTIP_STYLE}
          formatter={(v: number, name) => [
            formatValue ? formatValue(v) : v,
            name === 'income' ? 'Venituri' : 'Cheltuieli',
          ]}
        />
        <Legend
          iconType="circle"
          formatter={(value) => (value === 'income' ? 'Venituri' : 'Cheltuieli')}
          wrapperStyle={{ fontSize: 11, color: '#94A3B8' }}
        />
        <Bar dataKey="income" fill={FIRMIO_COLORS.green} radius={[4, 4, 0, 0]} />
        <Bar dataKey="expenses" fill={FIRMIO_COLORS.red} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
